<template>
  <Popover ref="itemPopover" :showHeader="true" :headerTitle="`${itemTitle}`">
    <div class="production-preview-panel-popover" v-if="orderItem">
      <div class="row">
        <div class="col-6 panel-popover-display">
          <template v-if="hasPrevStageTask && !isPrevStageExternal">
            <div class="font-weight-bold">Previous Location</div>
            <div>{{formatLookupName(prevStageTask.accountLocationId, accountLocationLookup)}}</div>
            <div class="font-weight-bold">Address</div>
            <div>{{formatLookupKey(prevStageTask.accountLocationId, accountLocationLookup, 'formattedAddress')}}</div>
          </template>
          <template v-if="hasPrevStageTask && isPrevStageExternal">
            <div class="font-weight-bold">Transfer From Supplier</div>
            <div>{{prevStageTask.purchaseOrder.contact.name}}</div>
            <div class="font-weight-bold">Purchase Order</div>
            <div><LinkPurchaseOrder :id="prevStageTask.purchaseOrder.id" :name="prevStageTask.purchaseOrder.sequenceNumber" /></div>
          </template>

          <FormHorizontalRule v-if="hasPrevStageTask && hasPanelStageTask" class="my-2" />

          <template v-if="hasPanelStageTask && !isStageTaskExternal">
            <div class="font-weight-bold">Destination Location</div>
            <div>{{formatLookupName(curStageTask.accountLocationId, accountLocationLookup)}}</div>
            <div class="font-weight-bold">Address</div>
            <div>{{formatLookupKey(curStageTask.accountLocationId, accountLocationLookup, 'formattedAddress')}}</div>
          </template>
          <template v-if="hasPanelStageTask && isStageTaskExternal">
            <div class="font-weight-bold">Transfer To Supplier</div>
            <div>{{curStageTask.purchaseOrder.contact.name}}</div>
            <div class="font-weight-bold">Purchase Order</div>
            <div><LinkPurchaseOrder :id="curStageTask.purchaseOrder.id" :name="curStageTask.purchaseOrder.sequenceNumber" /></div>
          </template>
          <template v-if="!hasPanelStageTask">
            <div class="font-weight-bold">Destination Location</div>
            <div>{{formatLookupName(order.accountLocationId, accountLocationLookup)}}</div>
            <div class="font-weight-bold">Address</div>
            <div>{{formatLookupKey(order.accountLocationId, accountLocationLookup, 'formattedAddress')}}</div>
          </template>
        </div>
        <div class="col-6 panel-popover-display">
          <template v-if="hasPanelStageTask">
            <div class="font-weight-bold">Transfer Status</div>
            <div>{{formatLookupName(curStageTask.transferStatus, ProductionTransferStatusTypeLookup)}}</div>
          </template>
        </div>
      </div>
      <div v-if="hasPanelTransferTasks">
        <FormHorizontalRule />
        <div class="font-weight-bold">Transfer Tasks</div>
        <div v-for="task of transferTasks" :key="task.id" class="row mt-2">
          <div class="col-3">
            <LinkTask :id="task.id" :name="task.sequenceNumber" />
          </div>
          <div class="col-3">
            <LabelBadge :labelClass="`label-task-status-${task.status}`">{{task.status | formatLookupName(TaskStatusTypeLookup)}}</LabelBadge>
          </div>
          <div class="col-6">
            <template v-if="task.status == TaskStatusTypes.ready || task.status == TaskStatusTypes.loading || task.status == TaskStatusTypes.inProgress">
              {{task.startedDate | formatDateTime}}
            </template>
            <template v-if="task.status == TaskStatusTypes.completed">
              {{task.completedDate | formatDateTime}}
            </template>
          </div>
        </div>
      </div>
    </div>
  </Popover>
</template>

<style scoped>
.production-preview-panel-popover {
  padding: 10px;
  font-size: 0.8rem;
  line-height: 1.1rem;
  min-width: 400px;
}
.panel-popover-display > div {
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
}
</style>

<script>
import _ from 'lodash'
import Popover from '@/components/popover.vue'

import { ProductionTransferStatusTypeLookup, TaskStatusTypes, TaskStatusTypeLookup, TaskItemStatusTypes, TaskItemTypes } from '@/store/types'

export default {
  name: 'OrderProductionTransferPopover',
  components: {
    Popover
  },
  computed: {
    itemTitle() {
      return 'Transfer Required'      
    },
    hasPrevStageTask() {
      return !!this.prevStageTask
    },
    hasPanelStageTask() {
      return !!this.curStageTask
    },
    isStageTaskExternal() {
      return this.curStageTask.isExternal
    },
    isPrevStageExternal() {
      return this.prevStageTask.isExternal
    },
    hasPanelTransferTasks() {
      return this.transferTasks.length > 0
    }
  },
  data() {
    return {
      orderItem: null,
      panel: null,
      prevStageTask: null,
      curStageTask: null,
      nextStageTask: null,
      productionStageId: null,
      transferTasks: [],
      accountLocationLookup: []
    }
  },
  created() {
    this.ProductionTransferStatusTypeLookup = ProductionTransferStatusTypeLookup
    this.TaskStatusTypes = TaskStatusTypes
    this.TaskStatusTypeLookup = TaskStatusTypeLookup
  },
  mounted() {
    this.requestData()    
  },
  methods: {
    requestData() {
      this.$apiService.adminService.getAccountLocations()
      .then(response => {
        this.accountLocationLookup = response.data.map(item => {
            return { value: item.id, name: item.name, formattedAddress: item.formattedAddress }
          })
      })
      .catch(err => {
        console.error(err)
      })
    },
    displayFromElement(element, order, orderItem, panel, orderTaskList, segmentInfo, prevStageTask, curStageTask, nextStageTask) {
      this.order = order
      this.orderItem = orderItem
      this.panel = panel
      this.productionStageId = segmentInfo.id
      this.prevStageTask = prevStageTask
      this.curStageTask = curStageTask
      this.nextStageTask = nextStageTask

      this.transferTasks = orderTaskList.filter(task => {
        return task.taskItems.some(taskItem => {
          if(taskItem.status == TaskItemStatusTypes.cancelled)
            return false
          if(taskItem.type != TaskItemTypes.transferInternal && taskItem.type != TaskItemTypes.transferExternal)
            return false

          if(taskItem.taskItemLinkedItems && panel) {
            return taskItem.taskItemLinkedItems.some(linkItem => {
              if(curStageTask) {
                return (linkItem.productionPanelId == panel.id && linkItem.productionStageId == curStageTask.productionStageId)
              }
              return (linkItem.productionPanelId == panel.id && linkItem.productionStageId == null)
            })
          }
          return false
        })
      })

      this.$refs.itemPopover.displayFromElement(element)
    },
  }
}
</script>